import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CaretLeft, CaretRight, Database, MagnifyingGlass, Trash, X,
} from "@phosphor-icons/react";

// v0.6.10 — the list used to be a `max-h-96 overflow-auto` box. With 32
// uploads that is a small window onto a long scroll inside an already
// scrolling page, and finding a file meant dragging a thin inner bar
// with no idea how far down it went. Ten to a page, with numbered
// pages, makes the size of the list visible and every file reachable
// in one click.
const PAGE_SIZE = 10;

// 1 … 4 5 6 … 12 — first, last and a window around the current page,
// so the pager stays one line however many uploads accumulate.
export const pageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const lo = Math.max(2, current - 1);
  const hi = Math.min(total - 1, current + 1);
  const out = [1];
  if (lo > 2) out.push("gap-lo");
  for (let i = lo; i <= hi; i += 1) out.push(i);
  if (hi < total - 1) out.push("gap-hi");
  out.push(total);
  return out;
};

// v0.6 P2 cap sweep — extracted verbatim from CalculatePanel.jsx.
// Production-files list + admin bulk-delete toolbar. Pure presentation:
// all state + mutation handlers stay with the parent panel.
export const ProductionRunsList = ({
  prodRuns,
  displayedProdRuns,
  hideDupes,
  duplicateCount,
  isAdmin,
  selectedCount,
  bulkDeleting,
  onBulkDelete,
  onDeleteAll,
  renderRunRow,
  query,
  onQueryChange,
}) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);

  const total = displayedProdRuns.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Deleting the last rows of the last page, or narrowing the search,
  // would otherwise leave the operator on an empty page.
  useEffect(() => {
    setPage((p) => Math.min(p, pageCount));
  }, [pageCount]);
  useEffect(() => {
    setPage(1);
  }, [query]);

  const from = (page - 1) * PAGE_SIZE;
  const pageRuns = displayedProdRuns.slice(from, from + PAGE_SIZE);

  const pageBtn =
    "min-w-[28px] h-7 px-2 rounded-lg text-[11px] border transition-colors "
    + "focus:outline-none focus:ring-2 focus:ring-teal-500/40 disabled:opacity-40 "
    + "disabled:cursor-not-allowed";

  return (
    <div className="border-t border-slate-200 dark:border-slate-700 p-5">
      <div data-testid="prod-runs-section">
        <div className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 mb-3 flex items-center justify-between gap-1">
          <span className="flex items-center gap-1.5">
            <Database size={12} weight="bold" /> {t("ui.calculate.production_files")} ({prodRuns.length}
            {hideDupes && duplicateCount > 0 && (
              <span className="ml-1 text-slate-500 dark:text-slate-400">
                · {t("ui.calculate.unique_count", { count: displayedProdRuns.length, defaultValue: `${displayedProdRuns.length} unique` })}
              </span>
            )}
            )
          </span>
          {isAdmin && prodRuns.length > 0 && (
            selectedCount > 0 ? (
              <button
                type="button"
                data-testid="bulk-delete-selected-btn"
                onClick={onBulkDelete}
                disabled={bulkDeleting}
                className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-lg transition-colors ${
                  bulkDeleting
                    ? "bg-slate-200 dark:bg-slate-800 text-slate-400 cursor-not-allowed"
                    : "bg-rose-600 hover:bg-rose-700 text-white"
                }`}
                title={t("ui.calculate.bulk_delete_tooltip", {
                  defaultValue: "Delete the selected uploads (skips files locked by a signed/frozen calculation)",
                })}
              >
                <Trash size={12} weight="bold" />
                {bulkDeleting
                  ? t("ui.calculate.bulk_delete_busy", { defaultValue: "Deleting…" })
                  : t("ui.calculate.bulk_delete_selected", {
                      count: selectedCount,
                      defaultValue: `Delete selected (${selectedCount})`,
                    })}
              </button>
            ) : (
              <button
                type="button"
                data-testid="bulk-delete-all-btn"
                onClick={onDeleteAll}
                disabled={bulkDeleting}
                className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-lg border transition-colors ${
                  bulkDeleting
                    ? "border-slate-200 dark:border-slate-800 text-slate-400 cursor-not-allowed"
                    : "border-rose-300 dark:border-rose-800 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40"
                }`}
                title={t("ui.calculate.delete_all_tooltip", {
                  defaultValue: "Delete all uploads (skips files locked by a signed/frozen calculation). Tip: click a filename to select individual files instead.",
                })}
              >
                <Trash size={12} weight="bold" />
                {bulkDeleting
                  ? t("ui.calculate.bulk_delete_busy", { defaultValue: "Deleting…" })
                  : t("ui.calculate.delete_all", {
                      count: prodRuns.length,
                      defaultValue: `Delete all (${prodRuns.length})`,
                    })}
              </button>
            )
          )}
        </div>
        {/* v0.6.10 — filename search. Only worth showing once there's
            something to search through; a handful of uploads scroll
            fine as-is. */}
        {prodRuns.length > 5 && (
          <div className="relative mb-2">
            <MagnifyingGlass
              size={13}
              weight="bold"
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 pointer-events-none"
            />
            <input
              type="search"
              data-testid="upload-search"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder={t("ui.calculate.upload_search_placeholder")}
              className="w-full pl-8 pr-8 py-2 text-xs rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-50 placeholder-slate-400 dark:placeholder-slate-500 focus:border-teal-500 dark:focus:border-teal-400 focus:ring-2 focus:ring-teal-500/30 outline-none"
            />
            {query && (
              <button
                type="button"
                data-testid="upload-search-clear"
                onClick={() => onQueryChange("")}
                aria-label={t("ui.client_selector.clear_search", "Clear search")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <X size={11} weight="bold" />
              </button>
            )}
          </div>
        )}
        {query && (
          <div
            data-testid="upload-search-count"
            className="text-[11px] text-slate-500 dark:text-slate-400 mb-2 px-1"
          >
            {t("ui.calculate.upload_search_match", { shown: displayedProdRuns.length, total: prodRuns.length })}
          </div>
        )}
        <div className="border border-slate-200 dark:border-slate-700 rounded-xl">
          {total === 0 ? (
            <div className="p-3 text-xs text-slate-600 dark:text-slate-300">
              {query
                ? t("ui.calculate.upload_search_no_match", { q: query })
                : t("ui.calculate.no_production_uploads")}
            </div>
          ) : (
            pageRuns.map(renderRunRow)
          )}
        </div>
        {pageCount > 1 && (
          <nav
            data-testid="upload-pager"
            aria-label={t("ui.calculate.pager_label", { defaultValue: "Upload pages" })}
            className="mt-2 flex items-center justify-between gap-3 flex-wrap"
          >
            <span className="text-[11px] text-slate-500 dark:text-slate-400">
              {t("ui.calculate.page_showing", {
                from: from + 1,
                to: Math.min(from + PAGE_SIZE, total),
                total,
              })}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                data-testid="upload-page-prev"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                aria-label={t("ui.calculate.page_prev", { defaultValue: "Previous page" })}
                className={`${pageBtn} border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:enabled:bg-slate-100 dark:hover:enabled:bg-slate-800`}
              >
                <CaretLeft size={11} weight="bold" />
              </button>
              {pageNumbers(page, pageCount).map((n) =>
                typeof n === "string" ? (
                  <span
                    key={n}
                    aria-hidden="true"
                    className="px-1 text-[11px] text-slate-400 dark:text-slate-500"
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={n}
                    type="button"
                    data-testid={`upload-page-${n}`}
                    onClick={() => setPage(n)}
                    aria-current={n === page ? "page" : undefined}
                    aria-label={t("ui.calculate.page_n", { n, defaultValue: `Page ${n}` })}
                    className={`${pageBtn} ${
                      n === page
                        ? "border-teal-500 bg-teal-50 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200 font-bold"
                        : "border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                    }`}
                  >
                    {n}
                  </button>
                ),
              )}
              <button
                type="button"
                data-testid="upload-page-next"
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                disabled={page === pageCount}
                aria-label={t("ui.calculate.page_next", { defaultValue: "Next page" })}
                className={`${pageBtn} border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:enabled:bg-slate-100 dark:hover:enabled:bg-slate-800`}
              >
                <CaretRight size={11} weight="bold" />
              </button>
            </div>
          </nav>
        )}
      </div>
    </div>
  );
};
